/**
 * @file Renders the Row, Column and Grid containers of a form or entity layout.
 * Consumers: `LayoutUI`, which renders the root Column and recurses through here.
 * Viewport cap: every Row, Grid and nested Column is capped at
 * `min(<px>, var(--form-content-max-width, 100%))`, so on a phone non-table content
 * fits the page that `FormPageContent` measures. The root Column takes the cap
 * unless one of its direct children is a Table: from `md` up an ArTable form keeps
 * document scroll and lets the table alone widen the page (below `md` the table
 * renders as cards and caps itself), so each non-table child of that
 * root sits in a capped `.form-content-cap` column, which `index.css` drops when
 * the child renders nothing so no stray gap opens. Grid collapses to one column
 * below antd `md` through `Col` responsive props; the persisted layout is untouched.
 */
import React, { FC, useMemo } from 'react';
import { Row, Col } from 'antd';
import { UIContainer, UIElementType } from '#types/System.types';
import { MAX_LIMITED_WIDTH_VIEWPORT } from '#types/System.Parameters.types';
import FlexBox from '../atoms/FlexBox';
import FlexCol from '../atoms/FlexCol';
import { FORM_PAGE_CSS_VARS } from '../atoms/FORM_PAGE_CSS_VARS';
import LayoutUI from './LayoutUI';
import type { ReadOnlyReason } from './readOnlyReason';

const formContentMaxWidth = `var(${FORM_PAGE_CSS_VARS.formContentMaxWidth}, 100%)`;

const viewportCap = (px: number) => `min(${px}px, ${formContentMaxWidth})`;

/**
 * Props for the ContainerUI component.
 * 
 * ContainerUI renders container elements (Row, Column, Grid) and manages their
 * layout, styling, and child element rendering.
 */
interface ContainerUIProps {
  /** The container element to render. Must be a Row, Column, or Grid type. */
  container: UIContainer;
  /** Whether the form/entity is in read-only/locked state. Disables interactive elements. */
  disabled?: boolean;
  /**
   * Why the artifact cannot be written, when that is worth telling the reader.
   * Narrower than `disabled` and forwarded verbatim to every child.
   */
  readOnlyReason?: ReadOnlyReason;
  /**
   * Whether this is a nested container (not the root).
   * 
   * Used to determine which width constraint applies. The root Column
   * (isNestedContainer=false) is capped at the form content width unless it
   * holds a Table, while nested Columns receive the columnMaxWidth constraint.
   * 
   * @default false
   */
  isNestedContainer?: boolean;
  /**
   * Maximum width for Row containers in pixels.
   * 
   * Applied to all Row containers via inline style maxWidth. Defaults to
   * MAX_LIMITED_WIDTH_VIEWPORT (1500px) if not provided.
   * 
   * @default MAX_LIMITED_WIDTH_VIEWPORT
   * @see MAX_LIMITED_WIDTH_VIEWPORT
   */
  rowMaxWidth?: number;
  /**
   * Maximum width for nested Column containers in pixels.
   * 
   * Applied only to Column containers when isNestedContainer is true. The root
   * Column (isNestedContainer=false) does not receive this constraint. Defaults
   * to MAX_LIMITED_WIDTH_VIEWPORT (1500px) if not provided.
   * 
   * @default MAX_LIMITED_WIDTH_VIEWPORT
   * @see MAX_LIMITED_WIDTH_VIEWPORT
   */
  columnMaxWidth?: number;
}

/**
 * ContainerUI - Component for rendering container elements (Row, Column, Grid).
 * 
 * This component handles the rendering of container types in the UI layout hierarchy.
 * It applies appropriate styling, manages child element rendering, and propagates
 * width constraints and nested container flags to child elements.
 * 
 * Key behaviors:
 * - Row containers: Always receive the viewport-capped rowMaxWidth
 * - Column containers: Nested columns receive the viewport-capped columnMaxWidth
 *   (uncapped when they hold a Table); the root column receives the form content
 *   width unless it holds a Table. Columns may shrink below their content width.
 * - Grid containers: Ant Design Row/Col, one column below md, evenly split from md
 * 
 * All child elements are rendered with isNestedContainer=true to ensure proper
 * width constraint propagation through the component tree.
 * 
 * @param props - The component properties
 * @param props.container - The container element structure to render
 * @param props.disabled - Whether the form/entity is in read-only mode
 * @param props.readOnlyReason - Why the artifact is read-only, when reportable
 * @param props.isNestedContainer - Whether this is a nested (not root) container
 * @param props.rowMaxWidth - Maximum width for Row containers
 * @param props.columnMaxWidth - Maximum width for nested Column containers
 * @returns The rendered container element or null if unknown type
 */
const ContainerUI: FC<ContainerUIProps> = React.memo(({
  container,
  disabled = false,
  readOnlyReason,
  isNestedContainer = false,
  rowMaxWidth = MAX_LIMITED_WIDTH_VIEWPORT,
  columnMaxWidth = MAX_LIMITED_WIDTH_VIEWPORT,
}) => {
  /**
   * Renders all child elements of the container.
   * 
   * All children are marked as nested containers (isNestedContainer=true) to ensure
   * proper width constraint propagation. This means any Column children will receive
   * the columnMaxWidth constraint, while the root Column (rendered from LayoutUI
   * with isNestedContainer=false) remains unconstrained.
   */
  const renderChildren = useMemo(() => {
    return container.children.map((child, index) => (
      <LayoutUI
        key={`${container.uiElementType}-${index}`}
        uiElement={child}
        disabled={disabled}
        readOnlyReason={readOnlyReason}
        isNestedContainer
        rowMaxWidth={rowMaxWidth}
        columnMaxWidth={columnMaxWidth}
      />
    ));
  }, [container.children, container.uiElementType, disabled, readOnlyReason, rowMaxWidth, columnMaxWidth]);

  /**
   * Renders the container based on its type (Row, Column, or Grid).
   * 
   * Each container type has specific styling and layout behaviors:
   * - Row: Horizontal flexbox with the viewport-capped maxWidth
   * - Column: Vertical flex column whose maxWidth depends on nesting and on holding a Table
   * - Grid: Ant Design responsive grid system
   */
  const content = useMemo(() => {
    switch (container.uiElementType) {
      /**
       * Row container: Horizontal flexbox layout.
       * 
       * All Row containers receive rowMaxWidth, capped at the form content width, to
       * limit horizontal expansion. Children are arranged horizontally with wrapping enabled.
       */
      case UIElementType.Row:
        return (
          <FlexBox
            gap={container.props?.gap || 10}
            wrap
            justifyStart
            alignStart
            stretch
            noGrow
            {...container.props}
            style={{
              maxWidth: viewportCap(rowMaxWidth),
              ...container.props?.style,
            }}
          >
            {renderChildren}
          </FlexBox>
        );
      /**
       * Column container: Vertical flex column layout.
       * 
       * Nested columns receive columnMaxWidth, capped at the form content width
       * unless they hold a Table. The root Column receives the form content width
       * unless it holds a Table, so an ArTable form widens with its table while its
       * other children are each capped at the form content width. The column may
       * shrink, so a single item wider than the line cannot overflow it.
       */
      case UIElementType.Column: {
        const holdsTable = container.children.some((child) => child.uiElementType === UIElementType.Table);
        const columnWidth = isNestedContainer
          ? { maxWidth: holdsTable ? columnMaxWidth : viewportCap(columnMaxWidth) }
          : !holdsTable && { maxWidth: formContentMaxWidth };
        const columnChildren = isNestedContainer || !holdsTable
          ? renderChildren
          : container.children.map((child, index) => child.uiElementType === UIElementType.Table
            ? renderChildren[index]
            : (
              <FlexCol
                key={`${container.uiElementType}-${index}`}
                noGrow
                noGap
                className="form-content-cap"
                style={{ width: '100%', maxWidth: formContentMaxWidth }}
              >
                {renderChildren[index]}
              </FlexCol>
            ));
        return (
          <FlexCol
            noGrow
            style={{
              alignItems: 'flex-start',
              justifyContent: 'flex-start',
              flexShrink: 1,
              minWidth: 0,
              ...columnWidth,
              ...container.props?.style,
            }}
            gap={container.props?.gap}
            {...container.props}
          >
            {columnChildren}
          </FlexCol>
        );
      }
      /**
       * Grid container: Ant Design responsive grid system.
       * 
       * Uses Ant Design's Row/Col components for responsive grid layouts.
       * Below md every child takes the full row; from md children are
       * distributed evenly across columns based on child count.
       */
      case UIElementType.Grid:
        return (
          <Row gutter={container.props?.gap || 10} style={{ maxWidth: viewportCap(rowMaxWidth) }}>
            {container.children.map((child, index) => (
              <Col key={`grid-${index}`} xs={24} md={24 / container.children.length}>
                <LayoutUI
                  uiElement={child}
                  disabled={disabled}
                  readOnlyReason={readOnlyReason}
                  isNestedContainer
                  rowMaxWidth={rowMaxWidth}
                  columnMaxWidth={columnMaxWidth}
                />
              </Col>
            ))}
          </Row>
        );
      default:
        console.warn(`Unknown container type: ${(container as any).uiElementType}`);
        return null;
    }
  }, [container, renderChildren, disabled, readOnlyReason, isNestedContainer, rowMaxWidth, columnMaxWidth]);

  return content;
});

ContainerUI.displayName = 'ContainerUI';

export default ContainerUI;